import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { GuestStatusInput } from "../../Inputs/GuestStatusInput";
import { isValidOptionColor, optionChipStyle } from "@/lib/utils/optionChipColor";

// Default token classes per GuestState — used when no custom color is set.
const DEFAULT_STATE_CLASS: Record<string, string> = {
  UNCONTACTED: "user-icon-font-1 user-icon-bg-1 outline-user-icon-1",
  CONTACTED: "user-icon-font-2 user-icon-bg-2 outline-user-icon-2",
  ACCEPTED: "user-icon-font-4 user-icon-bg-4 outline-user-icon-4",
  DECLINED: "user-icon-font-3 user-icon-bg-3 outline-user-icon-3",
  WAITLISTED: "user-icon-font-12 user-icon-bg-12 outline-user-icon-12",
};

type TableCellContentGuestStatusProps = {
  options: string[];
  /** Per-state custom colors (label -> hex); falls back to default tokens. */
  optionColors?: Record<string, string>;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  onFocusAction?: () => void;
  error?: string | null;
  border?: string;
  height?: string;
  surface?: string;
  fontSize?: string;
  rounded?: string;
  padding?: string;
  blank?: boolean;
  viewOnly?: boolean;
};

export const TableCellContentGuestStatus: React.FC<
  TableCellContentGuestStatusProps
> = ({
  options,
  value,
  border,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height,
  surface,
  fontSize,
  padding,
  rounded,
  blank,
  viewOnly,
  optionColors,
}) => {
  const t = useTranslations("optionsMultiSingleSelect");

  // Create a proper Map with original options as keys and translated/original values as values
  const optionsMap = useMemo(() => {
    const map = new Map<string, string>();
    options?.forEach((option) => {
      const displayValue = t(option);
      map.set(option, displayValue);
    });
    return map;
  }, [options, t]);

  // Convert the Map values to an array for the OptionInput
  const displayOptions = useMemo(() => {
    return Array.from(optionsMap.values());
  }, [optionsMap]);

  // Find the display value for the current value
  const currentDisplayValue = useMemo(() => {
    return optionsMap.get(value) || "";
  }, [optionsMap, value]);

  // Function to get the original key from a display value
  const getKeyFromDisplayValue = (displayValue: string): string => {
    for (const [key, val] of optionsMap.entries()) {
      if (val === displayValue) {
        return key;
      }
    }
    return displayValue; // Fallback to the display value if no key is found
  };

  return (
    <>
      {isEditing && isSelected ? (
        <div className="flex flex-col flex-1">
          <GuestStatusInput
            disabled={viewOnly}
            getKeyFromDisplayValue={getKeyFromDisplayValue}
            initialValue={currentDisplayValue}
            height={height}
            surface={surface}
            border={border}
            blank={blank}
            fontSize={fontSize}
            padding={padding}
            rounded={rounded}
            onFocusAction={onFocusAction}
            setParent={(selectedDisplayValue) => {
              // Convert the selected display value back to the original key
              const originalKey = getKeyFromDisplayValue(selectedDisplayValue);
              onChangeValue(originalKey);
            }}
            options={displayOptions}
          />
          {error && <p className="mt-1 body-3-regular opacity-0">{error}</p>}
        </div>
      ) : (
        <div className="w-full">
          {(() => {
            // Read the prop, not a ref written from an effect: an effect runs
            // AFTER the render that carried the new value and updating a ref
            // schedules nothing, so the chip kept showing the previous status
            // until some unrelated re-render came along.
            const customHex = optionColors?.[value];
            const hasCustom = isValidOptionColor(customHex);
            return (
              <p
                className={`w-max px-3 rounded-xl border ${height} flex items-center ${fontSize} ${
                  hasCustom ? "" : DEFAULT_STATE_CLASS[value] ?? ""
                }`}
                style={hasCustom ? optionChipStyle(customHex) : undefined}
              >
                {optionsMap.get(value) || value}
              </p>
            );
          })()}
        </div>
      )}
    </>
  );
};
